// @oasis-permission-inherits: /capcode (capcode:view)
import { useOnMountEffect } from "@mirage-web/utils/react-helpers";
import { Link, createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, ChevronRight, MessageSquare } from "lucide-react";
import * as React from "react";

import { Badge } from "~/components/ui/badge";
import { fetchInvestigations, investigationsWsUrl } from "~/modules/cap-code/network";
import {
  InvestigationUpdatedPayloadSchema,
  type ConversationEvent,
  type InvestigationRecord,
} from "~/modules/cap-code/schemas";

export const Route = createFileRoute("/_dash/capcode/investigations/")({
  component: InvestigationsRoute,
});

function InvestigationsRoute() {
  const [investigations, setInvestigations] = React.useState<InvestigationRecord[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const investigationEventCursorRef = React.useRef(0);

  const loadInvestigations = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setInvestigations(await fetchInvestigations(50));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load investigations");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadInvestigations();
  }, [loadInvestigations]);

  useOnMountEffect(() => {
    let closedByEffect = false;
    let reconnectAttempt = 0;
    let reconnectTimer: number | undefined;
    let socket: WebSocket | null = null;

    async function connect() {
      const url = await investigationsWsUrl(investigationEventCursorRef.current);
      if (closedByEffect) {
        return;
      }
      socket = new WebSocket(url);

      socket.addEventListener("open", () => {
        reconnectAttempt = 0;
      });

      socket.addEventListener("close", () => {
        if (closedByEffect) {
          return;
        }
        const delay = Math.min(1000 * 2 ** reconnectAttempt, 5000);
        reconnectAttempt += 1;
        reconnectTimer = window.setTimeout(() => {
          void connect();
        }, delay);
      });

      socket.addEventListener("error", () => {
        socket?.close();
      });

      socket.addEventListener("message", (message) => {
        const payload = JSON.parse(message.data as string) as {
          type: string;
          event?: ConversationEvent;
        };
        if (payload.type !== "event" || !payload.event) {
          return;
        }
        const event = payload.event;
        investigationEventCursorRef.current = Math.max(
          investigationEventCursorRef.current,
          event.cursor ?? 0
        );
        if (event.type !== "investigation.updated") {
          return;
        }
        const parsed = InvestigationUpdatedPayloadSchema.safeParse(event.payload);
        if (!parsed.success) {
          return;
        }
        setInvestigations((current) =>
          [
            parsed.data.investigation,
            ...current.filter((item) => item.id !== parsed.data.investigation.id),
          ]
            .sort((a, b) => b.updatedAt - a.updatedAt)
            .slice(0, 50)
        );
        setError(null);
      });
    }

    void connect();
    return () => {
      closedByEffect = true;
      if (typeof reconnectTimer !== "undefined") {
        window.clearTimeout(reconnectTimer);
      }
      socket?.close();
    };
  });

  const content = (() => {
    if (loading && investigations.length === 0) {
      return <InvestigationListSkeleton />;
    }
    if (investigations.length === 0) {
      return (
        <div className="rounded-lg border border-border bg-white p-10 text-center">
          <AlertTriangle className="mx-auto text-muted-foreground" size={28} />
          <h2 className="mt-3 text-sm font-semibold">No investigations yet</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Alertmanager incidents will appear here once they create conversations.
          </p>
        </div>
      );
    }
    return (
      <div className="overflow-hidden rounded-lg border border-border bg-white">
        {investigations.map((investigation) => (
          <InvestigationRow key={investigation.id} investigation={investigation} />
        ))}
      </div>
    );
  })();

  return (
    <div className="h-[calc(100dvh-57px)] overflow-y-auto bg-background">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-4 py-6">
        <header>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Investigations</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Alert-backed conversations and external events.
            </p>
          </div>
        </header>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {content}
      </div>
    </div>
  );
}

function InvestigationRow({ investigation }: { investigation: InvestigationRecord }) {
  return (
    <Link
      to="/capcode/investigations/$investigationId"
      params={{ investigationId: investigation.id }}
      className="flex items-center gap-4 border-b border-border p-4 transition-colors last:border-b-0 hover:bg-muted/50"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-background">
        <AlertTriangle size={18} className={statusIconClass(investigation)} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <h2 className="truncate text-sm font-semibold">{investigation.title}</h2>
          <StatusBadge investigation={investigation} />
          {investigation.severity && <Badge>{investigation.severity}</Badge>}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>{investigation.service ?? "unknown service"}</span>
          <span>{formatRelativeTime(investigation.updatedAt)}</span>
          <span className="inline-flex items-center gap-1">
            <MessageSquare size={12} />
            {investigation.conversationIds.length}
          </span>
          <span>{investigation.alertEvents.length} alerts</span>
        </div>
      </div>
      <ChevronRight size={16} className="shrink-0 text-muted-foreground" />
    </Link>
  );
}

function InvestigationListSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-white">
      {Array.from({ length: 4 }).map((_, index) => (
        <div
          key={index}
          className="flex items-center gap-4 border-b border-border p-4 last:border-b-0"
        >
          <div className="h-10 w-10 rounded-full bg-muted" />
          <div className="flex-1">
            <div className="h-4 w-1/2 rounded bg-muted" />
            <div className="mt-2 h-3 w-1/3 rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ investigation }: { investigation: InvestigationRecord }) {
  const className = statusBadgeClass(investigation.status);
  return <Badge className={className}>{investigation.status}</Badge>;
}

function statusIconClass(investigation: InvestigationRecord): string {
  if (investigation.status === "resolved" || investigation.status === "ignored") {
    return "text-emerald-600";
  }
  if (investigation.severity === "page" || investigation.severity === "critical") {
    return "text-red-600";
  }
  return "text-amber-600";
}

function statusBadgeClass(status: InvestigationRecord["status"]): string {
  if (status === "resolved" || status === "ignored") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (status === "mitigating") {
    return "border-blue-200 bg-blue-50 text-blue-700";
  }
  if (status === "monitoring") {
    return "border-sky-200 bg-sky-50 text-sky-700";
  }
  return "border-amber-200 bg-amber-50 text-amber-700";
}

function formatRelativeTime(value: number): string {
  const diff = Date.now() - value;
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < minute) {
    return "just now";
  }
  if (diff < hour) {
    return `${Math.floor(diff / minute)}m ago`;
  }
  if (diff < day) {
    return `${Math.floor(diff / hour)}h ago`;
  }
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(
    new Date(value)
  );
}
